import { useState, useEffect, useMemo, useCallback, memo, lazy, Suspense, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useUsageLimiter } from '../utils/usageLimiter';
import { createTranslator, getUserLanguage } from '../utils/detectLanguage';
import { homepagePtBR } from './mappings/homepage.pt-br';
import { homepageES } from './mappings/homepage.es';
import { useOptimizedAnimations } from './homePagePanel/useOptimizedAnimations';
import { useAuthPrompt } from '../hooks/useAuthPrompt';
import AuthPromptModal from './AuthPromptModal';
import StarCoinsDisplay from './starCoinSystem/StarCoinsDisplay';
import { OnboardingTour } from './homePagePanel/Onboarding';
import { useOnboarding } from './homePagePanel/Onboarding/hooks/useOnboarding';
import { useMobileBanner } from '../hooks/useMobileBanner';

import QuickActionsGridBase from './homePagePanel/QuickActionsGrid';
import BottomNavBarBase from './homePagePanel/BottomNavBar';
import UtilityBannerBase from './homePagePanel/UtilityBanner';

// Lazy load heavy components for better initial load performance
const PremiumUpsellBanner = lazy(() => import('./homePagePanel/PremiumUpsellBanner'));
const SportsFeed = lazy(() => import('./homePagePanel/SportsFeed'));
const HistorySection = lazy(() => import('./homePagePanel/HistorySection'));
const PromoCycler = lazy(() => import('./homePagePanel/PromoCards/PromoCycler'));
const InsightsCourt = lazy(() => import('./homePagePanel/InsightsCourt/InsightsCourt'));
const SEOFooter = lazy(() => import('./SEO/SEOFooter'));

// Memoize child components to prevent unnecessary re-renders
const QuickActionsGrid = memo(QuickActionsGridBase);
const BottomNavBar = memo(BottomNavBarBase);
const UtilityBanner = memo(UtilityBannerBase);

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex justify-center items-center py-8">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500"></div>
  </div>
);

// Move animation variants outside component to prevent recreation
const getPageVariants = (animationConfig) => animationConfig({
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1],
      staggerChildren: 0.08
    }
  }
});

const getItemVariants = (animationConfig, shouldReduce) => animationConfig({
  initial: { opacity: 0, y: shouldReduce ? 12 : 24, scale: shouldReduce ? 1 : 0.98 },
  animate: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
});

const HomePage = ({ user = null }) => {
  // ================== HOOKS & STATE MANAGEMENT ==================
  const { tier, starCoins } = useUsageLimiter(user?.uid);
  const [showBanner, setShowBanner] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const { shouldReduceAnimations, getAnimationConfig } = useOptimizedAnimations();
  const { isOpen, feature, title, showAuthPrompt, hideAuthPrompt } = useAuthPrompt();
  const { shouldShowOnboarding, completeOnboarding, skipOnboarding } = useOnboarding(user?.uid);
  const shouldReserveTopSpacing = Boolean(user);
  const topSpacerClassName = shouldReserveTopSpacing
    ? (isMobileView ? "h-36" : "h-20")
    : "h-5";

  // Use custom homepage banner on mobile
  useMobileBanner('homepage');

  // ================== TRANSLATION SETUP ==================
  // Stable translation function using ref pattern
  const translationRef = useRef();
  translationRef.current = createTranslator({
    'pt-br': homepagePtBR,
    'es': homepageES
  });

  const t = useCallback((text) => {
    return translationRef.current(text);
  }, []); // Empty deps - never recreates

  // ================== LIFECYCLE EFFECTS ==================
  useEffect(() => {
    localStorage.removeItem('hasSeenPremiumBanner');
    setMounted(true);

    // Check if mobile view
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);

    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);


  // ================== EVENT HANDLERS ==================
  // Stable callback for dismissing banner
  const dismissBanner = useCallback(() => {
    setShowBanner(false);
    localStorage.setItem('hasSeenPremiumBanner', 'true');
  }, []); // Already stable - no deps

  // ================== MEMOIZED VALUES ==================
  // Memoize animation variants to prevent recreation
  const pageVariants = useMemo(
    () => getPageVariants(getAnimationConfig),
    [getAnimationConfig]
  );

  const itemVariants = useMemo(
    () => getItemVariants(getAnimationConfig, shouldReduceAnimations),
    [getAnimationConfig, shouldReduceAnimations]
  );


  // ================== RENDER ==================
  return (
    <>
      {/* Onboarding Tour */}
      <OnboardingTour
        userId={user?.uid}
        autoStart={shouldShowOnboarding}
        onComplete={completeOnboarding}
        onSkip={skipOnboarding}
      />

      <motion.div
        className="min-h-screen relative overflow-hidden"
        initial="initial"
        animate="animate"
        variants={pageVariants}
      >


      {/* ================== BACKGROUND LAYERS ================== */}
      {/* Simple black background like F1Dash */}
      <div className="fixed inset-0 bg-black" />

      {/* ================== MAIN CONTENT CONTAINER ================== */}
      <div className="relative z-10 flex-1 overflow-y-auto">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pb-20" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {/* Premium spacing - Only reserve space when the auth header/banner exists */}
          <div className={topSpacerClassName} />

          {/* ================== FREE DAILY ACCESS INFO ================== */}
          {/* NOTE: Free daily access info (3 AI Chats, 1 X-Pick, 1 Prediction) is now shown in the BeforeBetChatCard within PromoCycler below to save space */}

          {/* ================== PROMO CARDS SECTION ================== */}
          <motion.div variants={itemVariants}>
            <Suspense fallback={<LoadingFallback />}>
              <PromoCycler user={user} showAuthPrompt={showAuthPrompt} />
            </Suspense>
          </motion.div>

          {/* ================== SPORTS FEED SECTION ================== */}
          <motion.div variants={itemVariants} className="mb-8 -mt-2">
            <Suspense fallback={<LoadingFallback />}>
              <div data-testid="sports-feed">
                <SportsFeed user={user} showAuthPrompt={showAuthPrompt} />
              </div>
            </Suspense>
          </motion.div>

          {/* ================== INSIGHTS COURT SECTION ================== */}
          <motion.div variants={itemVariants} className="mb-8">
            <Suspense fallback={<LoadingFallback />}>
              <InsightsCourt user={user} showAuthPrompt={showAuthPrompt} />
            </Suspense>
          </motion.div>

          {/* ================== PREMIUM UPSELL BANNER ================== */}
          <AnimatePresence>
            {showBanner && mounted && (
              <motion.div
                variants={itemVariants}
                initial="initial"
                animate="animate"
                exit={{
                  opacity: 0,
                  scale: 0.97,
                  y: -10,
                  filter: "blur(10px)"
                }}
                className="mb-6"
              >
                <div className="relative group">
                  {/* Glow effect on hover */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500" />

                  {/* Card with premium glass effect */}
                  <div className="relative bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/[0.05] overflow-hidden">
                    {/* Inner gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-emerald-500/5" />

                    <div className="relative">
                      <Suspense fallback={
                        <div className="h-44 bg-gradient-to-r from-purple-900/20 via-purple-800/20 to-amber-800/20 rounded-2xl animate-pulse" />
                      }>
                        <PremiumUpsellBanner onDismiss={dismissBanner} />
                      </Suspense>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>


          {/* ================== QUICK ACTIONS GRID ================== */}
          <motion.div
            variants={itemVariants}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
              <span className="text-white/40 text-xs font-medium tracking-wider uppercase">{t("Quick Actions")}</span>
            </div>

            <div className="bg-white/[0.02] backdrop-blur-sm rounded-2xl border border-white/[0.05] p-4">
              <QuickActionsGrid user={user} showAuthPrompt={showAuthPrompt} />
            </div>
          </motion.div>

          {/* ================== HISTORY/RECENT ACTIVITY SECTION ================== */}
          <motion.div
            variants={itemVariants}
            className="relative"
          >
            {/* Section label */}
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-white/40 text-xs font-medium tracking-wider uppercase">{t("Recent Activity")}</span>
            </div>

            {/* Multi-layer glass card */}
            <div className="relative">
              {/* Background layer */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] to-white/[0.01] rounded-2xl transform translate-y-1 scale-[0.98] blur-sm" />

              {/* Main card */}
              <div className="relative bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/[0.08] overflow-hidden shadow-2xl">
                {/* Top gradient accent */}
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />

                {/* Content with subtle padding and lazy loading */}
                <div className="p-4">
                  <Suspense fallback={<LoadingFallback />}>
                    <div data-testid="history-section">
                      <HistorySection userId={user?.uid} user={user} showAuthPrompt={showAuthPrompt} />
                    </div>
                  </Suspense>
                </div>

                {/* Bottom fade */}
                <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              </div>
            </div>
          </motion.div>

          {/* Premium bottom spacing */}
          <div className="h-12" />

          {/* ================== SEO FOOTER ================== */}
          <Suspense fallback={<div className="h-64" />}>
            <SEOFooter language={getUserLanguage()} />
          </Suspense>
        </div>
      </div>


      {/* ================== UTILITY BANNER (FULL WIDTH) ================== */}
      <UtilityBanner
        user={user}
        starCoins={starCoins}
        tier={tier}
        t={t}
        showAuthPrompt={showAuthPrompt}
      />

      {/* ================== DECORATIVE GRID OVERLAY ================== */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(0, 255, 255, 0.1) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0, 255, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}
      />

      {/* ================== BOTTOM NAVIGATION BAR ================== */}
      <div className="fixed bottom-0 inset-x-0 z-40">
        <div className="bg-black/60 backdrop-blur-2xl border-t border-white/[0.08]">
          <BottomNavBar user={user} showAuthPrompt={showAuthPrompt} />
        </div>
      </div>

      {/* ================== AUTH MODAL ================== */}
      <AuthPromptModal
        isOpen={isOpen}
        onClose={hideAuthPrompt}
        feature={feature}
        title={title}
      />

      {/* ================== STAR COINS DISPLAY (HIDDEN) ================== */}
      {/* This component is hidden but listens for the openStarCoinsModal event */}
      {/* The button is hidden but the modal needs to render normally */}
      {user?.uid && (
        <StarCoinsDisplay
          userId={user.uid}
          hideButton={true}
          isMobile={isMobileView}
        />
      )}
      {user?.uid && console.log('StarCoinsDisplay should be mounted for user:', user.uid)}
      </motion.div>
    </>
  );
};

// Export with custom comparison function for better performance
export default memo(HomePage, (prevProps, nextProps) => {
  // Only re-render if user ID changes
  return prevProps.user?.uid === nextProps.user?.uid;
});
